import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, UserGroupIcon, PuzzlePieceIcon, PlayIcon, TrophyIcon, ArrowRightOnRectangleIcon, ShieldCheckIcon, LinkIcon, EllipsisVerticalIcon, WrenchScrewdriverIcon, ChartBarIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { logout, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Only apply scroll behavior on desktop (sm and up)
      if (window.innerWidth >= 640) {
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          // Scrolling down and past 100px
          setIsScrolledDown(true);
        } else if (currentScrollY < lastScrollY) {
          // Scrolling up
          setIsScrolledDown(false);
        }
      } else {
        // On mobile, always show the nav
        setIsScrolledDown(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Main navigation items for bottom navigation bar (mobile) and desktop nav
  const mainNavigation = [
    { name: 'Home', href: '/', icon: HomeIcon },
    { name: 'Leagues', href: '/leagues', icon: TrophyIcon },
    { name: 'Game Library', href: '/games', icon: PuzzlePieceIcon },
    { name: 'Play Sessions', href: '/plays', icon: PlayIcon },
    { name: 'Players', href: '/players', icon: UserGroupIcon },
  ];

  // Secondary navigation items for hamburger menu (mobile) and desktop
  const secondaryNavigation = [
    { name: 'Admin', href: '/admin/allowlist', icon: ShieldCheckIcon },
    { name: 'Invites', href: '/admin/invites', icon: LinkIcon },
    { name: 'Usage', href: '/admin/usage', icon: ChartBarIcon },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-surface-base pb-20 sm:pb-0">
      {/* Top Navigation Bar - Overlay style on all pages */}
      <nav className={`fixed top-0 left-0 right-0 z-40 pointer-events-none sm:pointer-events-auto bg-transparent sm:bg-[var(--nav-frosted)] sm:backdrop-blur-lg sm:border-b sm:border-[var(--nav-border)] transition-transform duration-300 ${
        isScrolledDown ? 'sm:-translate-y-full' : 'sm:translate-y-0'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-12 sm:h-16">
            <div className="flex">
              {/* Desktop navigation */}
              <div className="hidden sm:flex sm:space-x-8">
                {mainNavigation.map((item) => {
                  const Icon = item.icon;
                  const testId = `nav-${item.name.toLowerCase().replace(/\s+/g, '-')}-desktop`;
                  return (
                    <Link
                      data-track={`nav_${item.name.toLowerCase().replace(/\s+/g, '_')}`}
                      key={item.name}
                      to={item.href}
                      data-testid={testId}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        isActive(item.href)
                          ? 'border-primary-500 text-heading'
                          : 'border-transparent text-body hover:border-edge-input hover:text-heading'
                      }`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Desktop menu dropdown */}
              <div className="hidden sm:block relative group">
                <button
                  data-track="nav_more"
                  className="inline-flex items-center px-2 py-2 text-sm font-medium rounded-md text-body hover:text-heading hover:bg-white/30 transition-all"
                >
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </button>
                <div className="absolute right-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150">
                  <div className="w-44 bg-surface rounded-lg shadow-lg border border-edge py-1">
                    <Link
                      data-track="nav_tools"
                      to="/tools"
                      className="flex items-center px-4 py-2 text-sm text-body hover:bg-surface-active"
                    >
                      <WrenchScrewdriverIcon className="h-4 w-4 mr-2 text-disabled" />
                      Game Tools
                    </Link>
                    {isAdmin && secondaryNavigation.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          data-track={`nav_${item.name.toLowerCase().replace(/\s+/g, '_')}`}
                          key={item.name}
                          to={item.href}
                          className="flex items-center px-4 py-2 text-sm text-body hover:bg-surface-active"
                        >
                          <Icon className="h-4 w-4 mr-2 text-disabled" />
                          {item.name}
                        </Link>
                      );
                    })}
                    <Link
                      data-track="nav_privacy"
                      to="/privacy"
                      className="flex items-center px-4 py-2 text-sm text-body hover:bg-surface-active"
                    >
                      <EyeIcon className="h-4 w-4 mr-2 text-disabled" />
                      Privacy
                    </Link>
                    <button
                      data-track="nav_logout"
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-body hover:bg-surface-active"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2 text-disabled" />
                      Logout
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto pt-0 sm:pt-[68px] sm:px-6 lg:px-8 -mt-12 sm:mt-0">
        <div className="px-4 py-6 sm:px-0 sm:py-0">
          {children}
          <p className="mt-8 mb-4 text-center text-xs text-muted" data-testid="telemetry-notice">
            We record how the site is used, including session replays (inputs are masked), to improve it.{' '}
            <Link data-track="nav_privacy_notice" to="/privacy" className="underline">Privacy &amp; opt-out</Link>
          </p>
        </div>
      </main>

      {/* Mobile "More" menu overlay */}
      {mobileMenuOpen && (
        <div className="sm:hidden fixed inset-0 z-50" onClick={closeMobileMenu}>
          <div className="absolute inset-0 bg-black/20" />
          <div
            className="absolute bottom-20 right-2 w-48 bg-surface rounded-lg shadow-lg border border-edge py-1 safe-area-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            <Link
              data-track="nav_tools"
              to="/tools"
              onClick={closeMobileMenu}
              className="flex items-center px-4 py-3 text-sm text-body hover:bg-surface-active"
            >
              <WrenchScrewdriverIcon className="h-5 w-5 mr-3 text-disabled" />
              Game Tools
            </Link>
            {isAdmin && secondaryNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  data-track={`nav_${item.name.toLowerCase().replace(/\s+/g, '_')}`}
                  key={item.name}
                  to={item.href}
                  onClick={closeMobileMenu}
                  className="flex items-center px-4 py-3 text-sm text-body hover:bg-surface-active"
                >
                  <Icon className="h-5 w-5 mr-3 text-disabled" />
                  {item.name}
                </Link>
              );
            })}
            <Link
              data-track="nav_privacy"
              to="/privacy"
              onClick={closeMobileMenu}
              className="flex items-center px-4 py-3 text-sm text-body hover:bg-surface-active"
            >
              <EyeIcon className="h-5 w-5 mr-3 text-disabled" />
              Privacy
            </Link>
            <button
              data-track="nav_logout"
              onClick={() => {
                handleLogout();
                closeMobileMenu();
              }}
              className="flex items-center w-full px-4 py-3 text-sm text-body hover:bg-surface-active"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-disabled" />
              Logout
            </button>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar - Mobile only (Material Design 3 style) */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 bg-[var(--nav-frosted)] backdrop-blur-md border-t border-[var(--nav-border)] z-50 safe-area-bottom">
        <div className="flex justify-around items-stretch h-20 px-2">
          {mainNavigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            const testId = `nav-${item.name.toLowerCase().replace(/\s+/g, '-')}-mobile`;
            return (
              <Link
                data-track={`nav_${item.name.toLowerCase().replace(/\s+/g, '_')}`}
                key={item.name}
                to={item.href}
                data-testid={testId}
                className={`flex flex-col items-center justify-center flex-1 min-w-0 py-2 px-1 border-t-2 transition-all duration-200 ${
                  active
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-muted hover:bg-surface-hover active:bg-surface-active'
                }`}
              >
                <div className="flex items-center justify-center w-16 h-8">
                  <Icon className="h-6 w-6 transition-all duration-200" />
                </div>
                <span className={`text-xs mt-1 font-medium w-full text-center leading-tight ${
                  active ? '' : 'opacity-80'
                }`}>
                  {item.name}
                </span>
              </Link>
            );
          })}
          {/* More button */}
          <button
            data-track="nav_more"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`flex flex-col items-center justify-center flex-1 min-w-0 py-2 px-1 border-t-2 transition-all duration-200 ${
              mobileMenuOpen
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-muted hover:bg-surface-hover active:bg-surface-active'
            }`}
          >
            <div className="flex items-center justify-center w-16 h-8">
              <EllipsisVerticalIcon className="h-6 w-6 transition-all duration-200" />
            </div>
            <span className={`text-xs mt-1 font-medium truncate w-full text-center ${
              mobileMenuOpen ? '' : 'opacity-80'
            }`}>
              More
            </span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Layout;